import { useEffect, useState } from 'react';
import { useBuildCommunicationConnectionMutation, useDeleteCommunicationConnectionMutation, useGetChannelsLazyQuery as _useGetChannelsLazyQuery, useGetCommunicationConnectionsLazyQuery as _useGetCommunicationConnectionsLazyQuery, useGetCommunicationConnectionsQuery, useGetCommunicationPreferencesQuery, useGetMessagesLazyQuery as _useGetMessagesLazyQuery, useGetMessagesMetadataQuery, useUpdateCommunicationMessageMutation, useUpdateCommunicationMessagesMutation, useUpdateCommunicationPreferencesMutation, } from '../generated/graphql-types';
export const getChannelHeaders = (input) => {
    if (!input) {
        return undefined;
    }
    const channelHeaders = input.flatMap((p) => p.channelPreferences.map((c) => ({
        id: c.id,
        displayName: c.displayName,
    })));
    return [...new Map(channelHeaders.map(c => [c.id, c])).values()];
};
export const convertPreferences = (input) => input?.map(p => ({
    id: p.id,
    displayName: p.displayName,
    channelPreferences: p.channelPreferences.reduce((obj, cp) => Object.assign(obj, { [cp.id]: cp.selected }), {}),
}));
export const convertData = (input) => ({
    channelHeaders: getChannelHeaders(input),
    preferences: convertPreferences(input),
});
export const convertPreferenceUpdates = (input) => input?.map(p => ({
    id: p.id,
    channelPreferences: Object.keys(p.channelPreferences).map(key => ({
        id: key,
        selected: p.channelPreferences[key],
    })),
}));
export const useGetCommunicationPreferences = (locale, notifyOnShare) => {
    const [currentLocale, setCurrentLocale] = useState(locale);
    const { data, error, loading, refetch, } = useGetCommunicationPreferencesQuery({
        fetchPolicy: 'no-cache',
        variables: {
            legacyNotifyOnShare: notifyOnShare,
        },
    });
    useEffect(() => {
        if (loading) {
            return;
        }
        if (locale !== currentLocale) {
            setCurrentLocale(locale);
            refetch();
        }
    }, [locale, loading]);
    const { channelHeaders, preferences } = convertData(data?.communicationPreferences);
    return {
        error,
        loading,
        channelHeaders,
        preferences,
    };
};
export const useGetChannelsLazyQuery = () => {
    const [getChannels, { data, error, loading }] = _useGetChannelsLazyQuery({
        fetchPolicy: 'no-cache',
    });
    return {
        getChannels,
        data: data?.channels,
        error,
        loading,
    };
};
export const useUpdateCommunicationPreferences = () => {
    const [mutateFn, { data, error, loading },] = useUpdateCommunicationPreferencesMutation({
        fetchPolicy: 'no-cache',
    });
    const mutate = (preferences) => {
        const update = convertPreferenceUpdates(preferences);
        return mutateFn({
            variables: {
                update,
            },
        });
    };
    const { channelHeaders, preferences } = convertData(data?.updateCommunicationPreferences);
    return {
        mutate,
        channelHeaders,
        preferences,
        error,
        loading,
    };
};
export const useUpdateMessageState = () => {
    const [mutateFn, { data, error, loading },] = useUpdateCommunicationMessageMutation();
    const updateMessageState = (update) => {
        return mutateFn({
            variables: {
                update,
            },
        });
    };
    return {
        updateMessageState,
        error,
        loading,
        data: data?.updateCommunicationMessage,
    };
};
export const useUpdateMessages = () => {
    const [mutateFn, { data, error, loading },] = useUpdateCommunicationMessagesMutation();
    const updateMessages = (update) => {
        return mutateFn({
            variables: {
                update,
            },
        });
    };
    return {
        updateMessages,
        error,
        loading,
        data: data?.updateCommunicationMessages,
    };
};
export const useGetMessagesLazyQuery = () => {
    const [_getMessages, { data, error, loading }] = _useGetMessagesLazyQuery({
        fetchPolicy: 'network-only',
    });
    const getMessages = (next, limit) => {
        return _getMessages({
            variables: {
                next,
                limit,
            },
        });
    };
    return {
        getMessages,
        error,
        loading,
        data: data?.communicationMessages,
    };
};
export const useGetMessagesMetadata = () => {
    const { data, loading, error, startPolling, stopPolling, refetch, } = useGetMessagesMetadataQuery({
        fetchPolicy: 'no-cache',
    });
    return {
        data,
        error,
        loading,
        startPolling,
        stopPolling,
        refetch,
    };
};
export const useGetConnectionsQuery = () => {
    const { data, loading, error } = useGetCommunicationConnectionsQuery({
        fetchPolicy: 'no-cache',
    });
    return {
        data: data?.communicationConnections,
        error,
        loading,
    };
};
export const useGetConnectionsLazyQuery = () => {
    const [getCommunicationConnections, { data, loading, error },] = _useGetCommunicationConnectionsLazyQuery({
        fetchPolicy: 'no-cache',
    });
    return {
        getCommunicationConnections,
        data: data?.communicationConnections,
        error,
        loading,
    };
};
export const useBuildCommunicationConnection = () => {
    const [mutateFn, { data, error, loading },] = useBuildCommunicationConnectionMutation();
    const buildConnection = (type, name, state) => {
        const loc = window.location;
        const url = `${loc.protocol}//${loc.host}`;
        return mutateFn({
            variables: {
                request: { type, name, state, url },
            },
        });
    };
    return {
        buildConnection,
        error,
        loading,
        data: data?.buildCommunicationConnection,
    };
};
export const useDeleteCommunicationConnection = () => {
    const [mutateFn, { data, error, loading },] = useDeleteCommunicationConnectionMutation();
    const deleteConnection = (id) => {
        return mutateFn({
            variables: {
                id,
            },
        });
    };
    return {
        deleteConnection,
        error,
        loading,
        data: data?.deleteCommunicationConnection,
    };
};
//# sourceMappingURL=coms-service.js.map